/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ----------------------------------------------------------------------------
 * Search By Vehicle section
 * ----------------------------------------------------------------------------
 **/

var SearchByVehicle = class extends HTMLElement {
  constructor() {
    super();

    this.container = this;
    this.sectionId = this.container.getAttribute('data-section-id');
    this.filterOpeners = this.container.querySelectorAll('.search-by-vehicle-placeholder');
    this.filterContainers = this.container.querySelectorAll('.search-by-vehicle-dropdown');
    this.filterValues = this.container.querySelectorAll('.search-by-vehicle-dropdown input');
    this.form = this.container.querySelector('form');
    this.searchButton = this.container.querySelector('.search-by-vehicle-search');
    this.searchURL = this.searchButton.getAttribute('href');
    this.searchIndex = 0;
    this.searchParams = [];

    this.init();

    document.addEventListener('shopify:section:load', this.onSectionLoad);

    document.addEventListener('facet:criteria-changed', async function() {
      console.log(this.sectionId);
      const searchByVehicle = document.querySelector('.search-by-vehicle');

      if (!searchByVehicle) {
        return;
      }

      const url = new URL(window.location);
      url.searchParams.set("section_id", this.sectionId);
      const response = await fetch(url.toString());
      const responseAsText = await response.text();
      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = responseAsText;

      searchByVehicle.replaceWith(fakeDiv.querySelector('.search-by-vehicle'));
    }.bind(this));
  }

  onSectionLoad(event) {
    if (event.detail.sectionId === this.sectionId) {
      this.init();
    }
  }

  init() {
    for (const opener of this.filterOpeners) {
      opener.addEventListener('click', this.toggleFilter.bind(this));
    }
    
    for (const filter of this.filterValues) {
      filter.addEventListener('change', this.filterChanged.bind(this));
    }
  }

  toggleFilter(event) {
    const target = event.target.tagName !== 'DIV' ? event.target.closest('.search-by-vehicle-placeholder') : event.target;

    this.closeAllFilters(target);
    
    target.classList.toggle('open');
  }

  closeAllFilters(elem = null) {
    for (const opener of this.filterOpeners) {
      if (elem && elem === opener) {
        continue;
      }
      
      opener.classList.remove('open');
    }
  }

  resetFilterState(elem) {
    const target = elem.parentNode.previousElementSibling;
    const newText = target.dataset.defaultPlaceholder;

    target.querySelector('span').innerText = newText;
  }

  getSearchQuery() {
    this.searchParams = [];

    const formData = new FormData(this.form);
    
    for (const key of formData.keys()) {
      const keyValues = formData.getAll(key);

      if (keyValues.length > 1 || (keyValues.length === 1 && Boolean(keyValues[0].trim()))) {
        let isExtended = false;

        for (const param of this.searchParams) {
          if (param[0] === key && !isExtended) {
            for (const elem of keyValues) {
              if (!param[1].includes(elem)) {
                param[1] += `,${ elem }`;
              }
            }

            isExtended = true;
          }
        }

        if (!isExtended) {
          this.searchParams.push([key, keyValues.join(',')]);
        }
      } else {
        for (const param of this.searchParams) {
          if (param[0] === key) {
            this.searchParams.splice(this.searchParams.indexOf(param), 1);

            break;
          }
        }
      }
    }

    let searchParamsAsString = '';
    let index = 0;

    for (const elem of this.searchParams) {
      searchParamsAsString += `${ elem[0] }=${ encodeURIComponent(elem[1]).replace(/%2C/g, ',') }`;

      if (index < this.searchParams.length - 1) {
        searchParamsAsString += '&';
        index++;
      }
    }

    if (searchParamsAsString.length > 0) {
      return { 
        url: `${ this.searchURL }?${ searchParamsAsString }&view=search-by-vehicle`,
        hasSearchParams: true 
      };
    } else {
      return { 
        url: this.searchURL,
        hasSearchParams: false
      };
    }
  }

  setFiltersState(data) {
    let isReset = false;

    for (const filterContainer of this.filterContainers) {
      const currentIndex = Number(filterContainer.getAttribute('data-search-index'));
      const filterElems = filterContainer.querySelectorAll('input');
      const resetInput = filterContainer.querySelector('[data-reset-input="true"]');

      const isSplit = filterContainer.getAttribute('data-is-split') === 'true' && currentIndex === this.searchIndex + 1;
      const filterName = isSplit ? this.handleize(filterContainer.getAttribute('data-filter-name')) : '';
      const relatedSplit = isSplit ? this.container.querySelector(`[data-search-index="${ this.searchIndex }"] input:checked`).value : '';
      const relatedValue = isSplit ? this.normalize(relatedSplit) : '';

      if (currentIndex < this.searchIndex) {
        continue;
      } else if (currentIndex === this.searchIndex && resetInput.checked === true && !isSplit) {
        isReset = true;
        const nextFilterElems = this.container.querySelectorAll(`[data-search-index="${ this.searchIndex + 1 }"] input`);

        for (const filterElem of nextFilterElems) {
          if (filterElem.getAttribute('data-reset-input') !== 'true') {
            filterElem.setAttribute('disabled', 'disabled');
            filterElem.checked = false;
          }
        }

        continue;
      } else if (currentIndex > this.searchIndex + 1 || isReset) {
        for (const filterElem of filterElems) {
          if (filterElem.getAttribute('data-reset-input') !== 'true') {
            filterElem.setAttribute('disabled', 'disabled');
            filterElem.checked = false;
          } else {
            filterElem.checked = true;

            this.resetFilterState(filterElem);
          }
        }

        continue;
      }

      /* Code Below: */
      /* currentIndex === this.searchIndex + 1 */
      /* OR */
      /* currentIndex === this.searchIndex && resetInput.checked === false */

      let currentData = null;

      if (isSplit) {
        for (const key in data) {
          if (key.includes(relatedValue) && key.includes(filterName)) {
            currentData = data[key];
          }
        }
      } else {
        currentData = data[filterContainer.querySelector('input:checked').getAttribute('name')]
      }

      for (const filterElem of filterElems) {
        for (const filterValue of currentData) {
          if (filterValue.param_name === filterElem.getAttribute('name') && filterValue.name === filterElem.getAttribute('value')) {
            if (filterValue.count > 0) {
              filterElem.removeAttribute('disabled');
            } else {
              filterElem.setAttribute('disabled', 'disabled');
              filterElem.checked = false;
            }
          }
        }
      }

      if (isSplit) {
        for (const filterElem of filterElems) {
          if (!filterElem.getAttribute('name').includes(relatedValue) || !filterElem.getAttribute('name').includes(filterName)) {
            filterElem.setAttribute('disabled', 'disabled');
            filterElem.checked = false;

            this.resetFilterState(filterElem);
          }
        }
      }
    }
  }

  handleize(str) {
    return str
            .toLowerCase()
            .replace("'", '')
            .replace(/[^\w\u00C0-\u024f]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
  }

  normalize(str) {
    return str
            .toLowerCase()
            .replace(/[^a-zA-Z0-9 ]/g, "")
            .replace(/\s/g, '');
  }

  filterChanged(event) {
    const elem = event.target;
    const index = Number(elem.parentNode.dataset.searchIndex);
    const placeholder = elem.parentNode.previousElementSibling;
    const instance = this;
    const last = index === this.filterContainers.length - 1;

    this.closeAllFilters();

    placeholder.classList.remove('open');
    placeholder.querySelector('span').innerText = elem.value !== '' ? elem.value : elem.nextElementSibling.innerText;

    this.searchIndex = index;

    if (!last) {
      this.form.classList.add('loading');

      const query = this.getSearchQuery();
      const queryString = `${ query.url }${ (query.hasSearchParams ? '&' : '?') }section_id=main-collection`;
  
      fetch(queryString, {
        type: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).then(function(response) {
        return response.text();
      }).then(function(html) {
        const tempElement = new DOMParser().parseFromString(html, 'text/html');
        const filterData = tempElement.querySelector('#search-by-vehicle-data');
        console.log(filterData.innerHTML);
        const data = JSON.parse(filterData.innerHTML);
  
        instance.setFiltersState(data);
        instance.searchButton.setAttribute('href', instance.getSearchQuery().url);
        instance.form.classList.remove('loading');
      });
    } else {
      this.searchButton.setAttribute('href', this.getSearchQuery().url);
    }
  }
}

window.customElements.define("search-by-vehicle", SearchByVehicle);